import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fmtMYT } from "@/lib/myt";
import { ArrowRight, BadgeCheck, CheckCircle2, ChevronLeft, ChevronRight, Clock, Lock, Trophy, Truck, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import mountainBg from "@/assets/reward-mall-mountains.jpg";

// reward eligibility -> gallery restriction label (claim is gated by status === 'eligible' only)
const RESTRICTION = {
  eligible: { label: "Eligible", cls: "text-emerald-400", icon: CheckCircle2 },
  in_progress: { label: "Locked", cls: "text-[#8A93A6]", icon: Lock },
  locked: { label: "Locked", cls: "text-[#8A93A6]", icon: Lock },
  pending: { label: "Pending review", cls: "text-[#D4AF37]", icon: Clock },
  approved: { label: "Approved", cls: "text-emerald-400", icon: CheckCircle2 },
  processing: { label: "Processing", cls: "text-[#D4AF37]", icon: Clock },
  shipped: { label: "Shipped", cls: "text-emerald-400", icon: Truck },
  redeemed: { label: "Unlocked", cls: "text-emerald-400", icon: BadgeCheck },
};

const REQUEST_STYLES = {
  pending: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  approved: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  processing: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  shipped: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  rejected: "text-red-400 bg-red-400/10 border-red-400/30",
  delivered: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
};

const MAP_ROW_H = 118;
const nodeXpct = (i) => (i % 2 === 0 ? 30 : 44);
// hide preview-only seed artifacts (e.g. TEST_Phase7_Temp_Rank) from the member UI
const isRealRank = (r) => !/^TEST[_\s-]/i.test((r && r.name) || "");

function progressText(reward) {
  if (reward.progress?.rank) {
    return `${reward.progress.rank.current_name} → ${reward.progress.rank.required_name}`;
  }
  return "";
}

function Medallion({ state }) {
  const size = state === "current" ? 64 : 52;
  const gold = state === "future" ? "#6B7280" : "#E5C158";
  const ring =
    state === "current"
      ? "border-[3px] border-[#E5C158]"
      : state === "completed"
      ? "border-2 border-[#D4AF37]/80"
      : "border-2 border-[#2C3140]";
  return (
    <div
      className={`relative grid place-items-center rounded-full bg-[#0B0C10] ${ring} ${state === "current" ? "rm-node-glow" : ""}`}
      style={{
        width: size,
        height: size,
        boxShadow: state === "future" ? "none" : "0 0 18px 2px rgba(212,175,55,.35)",
      }}
    >
      <Trophy className="drop-shadow" style={{ width: size * 0.46, height: size * 0.46, color: gold }} strokeWidth={2} />
      {state === "future" && (
        <span className="absolute -bottom-1 -right-1 grid place-items-center h-5 w-5 rounded-full bg-[#14161C] border border-[#2C3140]">
          <Lock className="h-2.5 w-2.5 text-[#8A93A6]" />
        </span>
      )}
    </div>
  );
}

export default function MemberRewards() {
  const [data, setData] = useState(null);
  const [rankInfo, setRankInfo] = useState(null);
  const [selectedRank, setSelectedRank] = useState(null);
  const [view, setView] = useState("map"); // "map" | "rewards" — map-first navigation
  const [detail, setDetail] = useState(null);
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [redeemError, setRedeemError] = useState("");
  const [feedbackMine, setFeedbackMine] = useState({});
  useEffect(() => { api.get("/member/feedback/mine").then((res) => setFeedbackMine(res.data || {})).catch(() => {}); }, []);
  const mapScrollRef = useRef(null);
  const rootRef = useRef(null);
  const navigate = useNavigate();

  const load = () => {
    api.get("/member/rewards").then((res) => setData(res.data)).catch(() => setData({ rewards: [], requests: [], ranks: [] }));
    api.get("/member/profile").then((res) => setRankInfo(res.data.rank)).catch(() => {});
  };

  useEffect(load, []);

  // center the map on the member's current rank once data is ready
  useEffect(() => {
    if (!data) return;
    const ranks = [...(data.ranks || [])].filter(isRealRank).sort((a, b) => a.level - b.level);
    const c = mapScrollRef.current;
    if (!c || !ranks.length) return;
    const startLvl = selectedRank ?? (data.current_rank_level > 0 ? data.current_rank_level : ranks[0].level);
    const idx = ranks.findIndex((r) => r.level === startLvl);
    if (idx >= 0) c.scrollTop = Math.max(0, idx * MAP_ROW_H + MAP_ROW_H / 2 - c.clientHeight / 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // re-center the map on the selected/current rank when returning from the rewards view
  useEffect(() => {
    if (view !== "map" || !data) return;
    const ranks = [...(data.ranks || [])].filter(isRealRank).sort((a, b) => a.level - b.level);
    const c = mapScrollRef.current;
    if (!c || !ranks.length) return;
    const startLvl = selectedRank ?? (data.current_rank_level > 0 ? data.current_rank_level : ranks[0].level);
    const idx = ranks.findIndex((r) => r.level === startLvl);
    if (idx >= 0) c.scrollTop = Math.max(0, idx * MAP_ROW_H + MAP_ROW_H / 2 - c.clientHeight / 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // pause continuous animations when the tab is hidden (no polling / no rAF)
  useEffect(() => {
    const onVis = () => {
      const el = rootRef.current;
      if (!el) return;
      if (document.hidden) el.classList.add("rm-anim-paused");
      else el.classList.remove("rm-anim-paused");
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const redeem = async (reward) => {
    setRedeemBusy(true);
    setRedeemError("");
    try {
      await api.post(`/member/rewards/${reward.id}/redeem`);
      toast.success("Redemption request submitted");
      setDetail(null);
      load();
    } catch (err) {
      setRedeemError(formatApiError(err));
    } finally {
      setRedeemBusy(false);
    }
  };

  const openReward = (reward) => { setRedeemError(""); setDetail(reward); };

  if (!data) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="reward-mall-loading">
        <div className="h-12 w-48 bg-[#121318] rounded-xl" />
        <div className="card-lux h-24" />
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="card-lux h-[520px]" />
          <div className="card-lux h-[520px]" />
        </div>
      </div>
    );
  }

  const sortedRanks = [...(data.ranks || [])].filter(isRealRank).sort((a, b) => a.level - b.level);
  const lowest = sortedRanks[0];
  const cur = data.current_rank_level || 0;

  // group rewards by rank level (null / unknown requirement -> lowest rank bucket)
  const bucket = new Map();
  sortedRanks.forEach((r) => bucket.set(r.level, []));
  (data.rewards || []).forEach((rw) => {
    let lvl = rw.required_rank_level;
    if (lvl === null || lvl === undefined || !bucket.has(lvl)) lvl = lowest ? lowest.level : lvl;
    if (bucket.has(lvl)) bucket.get(lvl).push(rw);
  });
  bucket.forEach((arr) => arr.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)));

  const activeSelected = selectedRank ?? (cur > 0 ? cur : lowest ? lowest.level : null);
  const galleryRewards = activeSelected != null ? bucket.get(activeSelected) || [] : [];
  const selectedRankObj = sortedRanks.find((r) => r.level === activeSelected);
  const selectedLocked = activeSelected != null && activeSelected > cur;

  const stateOf = (lvl) => (lvl < cur ? "completed" : lvl === cur ? "current" : "future");

  const selectRank = (lvl) => {
    setSelectedRank(lvl);
    setView("rewards");
  };

  const backToMap = () => setView("map");

  // header progress (authoritative source: /member/profile -> rank)
  const curName = rankInfo?.current?.name || data.current_rank_name || "No Rank";
  const nextName = rankInfo?.next?.name || null;
  const pct = Math.max(0, Math.min(100, Math.round(rankInfo?.progress_pct ?? 0)));

  const innerH = sortedRanks.length * MAP_ROW_H;
  const pts = sortedRanks.map((_, i) => ({ x: nodeXpct(i), y: i * MAP_ROW_H + MAP_ROW_H / 2 }));
  let pathD = "";
  if (pts.length) {
    pathD = `M ${pts[0].x} 0 L ${pts[0].x} ${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const p0 = pts[i - 1];
      const p1 = pts[i];
      const my = (p0.y + p1.y) / 2;
      pathD += ` C ${p0.x} ${my}, ${p1.x} ${my}, ${p1.x} ${p1.y}`;
    }
    pathD += ` L ${pts[pts.length - 1].x} ${innerH}`;
  }
  const particleYs = [0.14, 0.4, 0.66, 0.88];

  return (
    <div className="space-y-6" data-testid="reward-mall-page" ref={rootRef}>
      {/* ===== Title ===== */}
      <div>
        <p className="eyebrow-label mb-1">Member Portal</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight" data-testid="reward-mall-heading">
          Reward Mall
        </h1>
        <p className="mt-1 text-sm sm:text-base text-[#94A3B8]">Your reward journey</p>
      </div>

      {/* ===== Current-rank summary ===== */}
      <div className="card-lux relative overflow-hidden p-4 sm:p-5" data-testid="reward-progress-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div className="flex items-center gap-3 shrink-0">
            <div
              className="grid place-items-center h-12 w-12 text-[#E5C158] border-2 border-[#D4AF37]/70 bg-[#0B0C10]"
              style={{ clipPath: "polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%)" }}
            >
              <Trophy className="h-5 w-5" strokeWidth={2} />
            </div>
            <div>
              <p className="eyebrow-label">Current rank</p>
              <p className="font-heading text-lg font-bold text-white" data-testid="reward-mall-rank-chip">{curName}</p>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2.5 rounded-full bg-[#0B0C10] border border-[#222530] overflow-hidden">
                <div className="h-full rounded-full rm-bar-shimmer transition-[width] duration-700" style={{ width: `${pct}%` }} />
              </div>
              <span className="font-mono text-sm text-[#E5C158] shrink-0" data-testid="reward-progress-pct">{pct}%</span>
            </div>
          </div>
          <div className="shrink-0 sm:text-right sm:border-l sm:border-[#222530] sm:pl-6">
            <p className="eyebrow-label">Next rank</p>
            <p className="font-heading text-base font-bold text-white">{nextName || "Highest rank"}</p>
          </div>
        </div>
      </div>

      {/* ===== Rank journey map (initial / map-first view) ===== */}
      {view !== "rewards" && (
        <div className="card-lux relative overflow-hidden" data-testid="reward-map">
          <img src={mountainBg} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-90 pointer-events-none" />
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-[#050506]/90 via-[#050506]/35 to-[#050506]/70" />
          <div className="absolute inset-x-0 top-0 h-24 pointer-events-none bg-gradient-to-b from-[#050506] to-transparent" />
          <div className="relative">
            <div className="p-5">
              <h2 className="font-heading text-lg font-bold text-white">Your rank journey</h2>
              <p className="text-xs text-[#94A3B8] mt-0.5">Select any rank to explore its rewards.</p>
            </div>
            <div ref={mapScrollRef} className="relative overflow-y-auto max-h-[480px] lg:max-h-[560px]" data-testid="reward-map-scroll">
              <div className="relative" style={{ height: innerH }}>
                <svg width="100%" height={innerH} viewBox={`0 0 100 ${innerH}`} preserveAspectRatio="none" className="absolute inset-0 pointer-events-none" aria-hidden="true">
                  <defs>
                    <linearGradient id="rmGold" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#9C7C28" />
                      <stop offset="0.5" stopColor="#E5C158" />
                      <stop offset="1" stopColor="#9C7C28" />
                    </linearGradient>
                  </defs>
                  <path d={pathD} fill="none" stroke="url(#rmGold)" strokeWidth="4" strokeLinecap="round" opacity="0.85" vectorEffect="non-scaling-stroke" />
                  <path d={pathD} className="rm-path-flow" fill="none" stroke="#F7E9AE" strokeWidth="4" strokeLinecap="round" opacity="0.9" vectorEffect="non-scaling-stroke" />
                </svg>
                {particleYs.map((f, i) => (
                  <span
                    key={i}
                    className="rm-particle absolute h-1 w-1 rounded-full bg-[#F7E9AE] pointer-events-none"
                    style={{ left: `${nodeXpct(Math.round(f * (sortedRanks.length - 1)))}%`, top: innerH * f, animationDelay: `${i}s`, boxShadow: "0 0 6px rgba(212,175,55,.8)" }}
                  />
                ))}
                {sortedRanks.map((r, i) => {
                  const st = stateOf(r.level);
                  const isSel = activeSelected === r.level;
                  const count = (bucket.get(r.level) || []).length;
                  return (
                    <button
                      key={r.level}
                      onClick={() => selectRank(r.level)}
                      className="absolute inset-x-0 flex items-center pointer-events-auto text-left group"
                      style={{ top: i * MAP_ROW_H, height: MAP_ROW_H }}
                      data-testid={`reward-rank-node-${r.level}`}
                      aria-label={`${r.name} rank`}
                      aria-pressed={isSel}
                    >
                      <span className="absolute" style={{ left: `${pts[i].x}%`, top: "50%", transform: "translate(-50%,-50%)" }}>
                        <span className={`relative block rounded-full ${isSel && st !== "current" ? "ring-2 ring-[#E5C158] ring-offset-2 ring-offset-[#050506]" : ""} rounded-full`}>
                          <Medallion state={st} />
                        </span>
                      </span>
                      <span className="absolute" style={{ left: `${pts[i].x}%`, top: "50%", transform: "translateY(-50%)", paddingLeft: st === "current" ? 46 : 40, right: 12 }}>
                        {st === "current" && (
                          <span className="inline-block mb-1 text-[9px] font-mono tracking-[0.25em] uppercase text-[#050506] bg-[#E5C158] rounded-full px-2 py-0.5" data-testid="reward-current-label">Current</span>
                        )}
                        {isSel && st !== "current" && (
                          <span className="inline-block mb-1 text-[9px] font-mono tracking-[0.25em] uppercase text-[#E5C158] border border-[#E5C158]/50 rounded-full px-2 py-0.5" data-testid="reward-viewing-label">Viewing</span>
                        )}
                        <p className={`font-heading font-bold leading-tight truncate ${st === "current" ? "text-lg text-white" : st === "completed" ? "text-base text-white" : "text-base text-[#8A93A6]"}`}>{r.name}</p>
                        <span className={`inline-flex items-center gap-1 text-xs mt-0.5 ${st === "future" ? "text-[#6B7280]" : "text-[#E5C158]"} group-hover:gap-1.5 transition-all`}>
                          {count > 0 ? "View rewards" : "No rewards"} <ArrowRight className="h-3 w-3" />
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Rank rewards view (opened from a map node) ===== */}
      {view === "rewards" && (
        <div className="space-y-4" data-testid="reward-rank-view">
          <button
            onClick={backToMap}
            className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-[#E5C158] hover:text-white transition-colors"
            data-testid="reward-back-to-map"
          >
            <ChevronLeft className="h-4 w-4" /> Back to map
          </button>
          <div className="card-lux p-5" data-testid="reward-gallery">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="font-heading text-xl font-bold text-white truncate" data-testid="reward-gallery-rank-name">{selectedRankObj ? selectedRankObj.name : "—"}</h2>
              <p className="text-xs text-[#94A3B8] mt-1">Explore every reward. Claim when eligible.</p>
            </div>
            {selectedLocked && (
              <span className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-[#8A93A6] border border-[#2C3140] rounded-full px-3 py-1.5" data-testid="reward-claim-locked-pill">
                <Lock className="h-3 w-3" /> Claim locked
              </span>
            )}
          </div>

          <div className="flex items-center justify-between mt-4 mb-3">
            <p className="text-xs font-mono text-[#94A3B8]" data-testid="reward-gallery-count">{galleryRewards.length} {galleryRewards.length === 1 ? "reward" : "rewards"}</p>
            {galleryRewards.length > 0 && <p className="text-[11px] font-mono text-[#5B6270]">All rewards shown</p>}
          </div>

          <div key={activeSelected} className="rm-gallery-enter">
            {galleryRewards.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[#222530] bg-[#0B0C10]/50 px-4 py-10 text-center text-sm text-[#5B6270]" data-testid="reward-gallery-empty">
                No rewards at this rank yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {galleryRewards.map((rw) => {
                  const meta = RESTRICTION[rw.status] || RESTRICTION.locked;
                  return (
                    <div key={rw.id} className="rounded-2xl border border-[#222530] bg-[#0B0C10]/70 overflow-hidden flex flex-col" data-testid={`reward-map-card-${rw.id.slice(-6)}`}>
                      <div className="relative aspect-[4/3] bg-[#0B0C10]">
                        {rw.image_url ? (
                          <img src={rw.image_url} alt={rw.name} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full grid place-items-center text-[#222530]"><Trophy className="h-8 w-8" /></div>
                        )}
                      </div>
                      <div className="p-3 flex-1 flex flex-col">
                        <p className="font-heading text-sm font-semibold text-white leading-tight line-clamp-2">{rw.name}</p>
                        <span className={`mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-mono ${meta.cls}`}>
                          <meta.icon className="h-3 w-3" /> {meta.label}
                        </span>
                        <button
                          onClick={() => openReward(rw)}
                          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[#E5C158] border border-[#D4AF37]/40 rounded-lg py-2 hover:bg-[#D4AF37]/10 transition-colors"
                          data-testid={`reward-view-details-${rw.id.slice(-6)}`}
                        >
                          View details <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* ===== Detail modal (existing claim flow, unchanged) ===== */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="reward-detail-modal">
          <div className="absolute inset-0 bg-black/80" onClick={() => setDetail(null)} />
          <div className="card-lux relative w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="relative h-56 sm:h-64 bg-[#0B0C10]">
              {detail.image_url ? (
                <img loading="lazy" src={detail.image_url} alt={detail.name} className="w-full h-full object-cover" data-testid="reward-detail-image" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#222530]"><Trophy className="h-12 w-12" /></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#121318] via-transparent to-transparent" />
              <button
                onClick={() => setDetail(null)}
                className="absolute top-3 right-3 h-8 w-8 rounded-full bg-[#050506]/70 backdrop-blur border border-[#222530] flex items-center justify-center text-[#94A3B8] hover:text-white transition-colors"
                data-testid="reward-detail-close-button"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="absolute bottom-3 left-4">
                {(() => { const m = RESTRICTION[detail.status] || RESTRICTION.locked; return (
                  <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border border-[#222530] bg-[#050506]/70 backdrop-blur ${m.cls}`}>
                    <m.icon className="h-3 w-3" /> {m.label}
                  </span>
                ); })()}
              </div>
            </div>
            <div className="p-6 sm:p-8">
              <p className="eyebrow-label mb-2">{detail.category}</p>
              <h3 className="font-heading text-xl font-bold text-white" data-testid="reward-detail-name">{detail.name}</h3>
              <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">{detail.full_description || detail.short_description}</p>

              <div className="gold-divider my-5" />

              <p className="eyebrow-label mb-3">Requirements</p>
              <div className="space-y-2 text-sm">
                {detail.progress?.rank ? (
                  <div className="flex items-center justify-between border border-[#222530] rounded-lg px-4 py-2.5">
                    <span className="text-[#94A3B8] text-xs">Rank {detail.progress.rank.required_name}+</span>
                    <span className={`text-xs font-mono ${detail.progress.rank.current_level >= detail.progress.rank.required_level ? "text-emerald-400" : "text-[#64748B]"}`}>
                      You: {detail.progress.rank.current_name}
                    </span>
                  </div>
                ) : (
                  <div className="border border-[#222530] rounded-lg px-4 py-2.5">
                    <span className="text-[#94A3B8] text-xs">Open to all members</span>
                  </div>
                )}
              </div>

              {detail.redemption_instructions && (
                <>
                  <p className="eyebrow-label mb-3 mt-5">How Redemption Works</p>
                  <p className="text-xs text-[#94A3B8] leading-relaxed">{detail.redemption_instructions}</p>
                </>
              )}

              {redeemError && redeemError.startsWith("ADDRESS_INCOMPLETE") ? (
                <div className="mt-5 text-sm text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg px-4 py-4" data-testid="reward-address-required">
                  <p>Please complete your delivery address before claiming this reward.</p>
                  <button onClick={() => navigate("/member/profile")} className="btn-gold w-full py-2.5 text-xs mt-3" data-testid="reward-complete-address-button">
                    Complete Address
                  </button>
                </div>
              ) : redeemError ? (
                <p className="mt-5 text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="reward-detail-error">{redeemError}</p>
              ) : null}

              <div className="mt-6">
                {detail.status === "eligible" ? (
                  <button onClick={() => redeem(detail)} disabled={redeemBusy} className="btn-gold w-full py-3 text-sm disabled:opacity-60" data-testid="reward-detail-redeem-button">
                    {redeemBusy ? "Submitting..." : "Claim This Reward"}
                  </button>
                ) : detail.status === "pending" ? (
                  <p className="text-center text-xs font-mono tracking-widest uppercase text-[#D4AF37] border border-[#D4AF37]/30 rounded-lg py-3">Request under review</p>
                ) : detail.status === "approved" ? (
                  <p className="text-center text-xs font-mono tracking-widest uppercase text-emerald-400 border border-emerald-400/30 rounded-lg py-3">Approved — delivery in progress</p>
                ) : (
                  <p className="text-center text-xs font-mono tracking-widest uppercase text-[#64748B] border border-[#222530] rounded-lg py-3" data-testid="reward-detail-locked-note">
                    {progressText(detail) ? `Progress: ${progressText(detail)}` : "Locked — keep progressing"}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== My redemption requests (unchanged) ===== */}
      <div className="card-lux overflow-hidden">
        <div className="px-6 pt-6">
          <p className="eyebrow-label">My Redemption Requests</p>
          <p className="text-xs text-[#94A3B8] mt-1" data-testid="redemption-feedback-notice">Every 5-star feedback earns 1 PLUSMAKER coin.</p>
        </div>
        <div className="divide-y divide-[#222530] mt-4" data-testid="member-redemptions-list">
          {data.requests.length === 0 ? (
            <p className="px-6 pb-8 pt-2 text-sm text-[#64748B]">No redemption requests yet.</p>
          ) : (
            data.requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-4 px-6 py-4" data-testid={`member-redemption-${r.id.slice(-6)}`}>
                <div>
                  <p className="text-sm font-medium text-white">{r.reward_name}</p>
                  <p className="text-xs text-[#64748B]">
                    {r.created_at ? fmtMYT(r.created_at) : ""}
                    {r.admin_note ? ` · ${r.admin_note}` : ""}
                  </p>
                  {r.tracking_number && (
                    <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-mono text-emerald-400" data-testid={`member-tracking-${r.id.slice(-6)}`}>
                      <Truck className="h-3 w-3" /> Tracking: {r.tracking_number}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                  <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-1.5 border ${REQUEST_STYLES[r.status] || REQUEST_STYLES.pending}`}>
                    {r.status}
                  </span>
                  {r.status === "delivered" && (
                    feedbackMine[`trade_redeem:${r.id}`] ? (
                      <button onClick={() => navigate(`/member/feedback?highlight=${feedbackMine[`trade_redeem:${r.id}`].id}`)} className="text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-1.5 border border-[#2a2d38] text-[#94A3B8] hover:text-white" data-testid={`view-feedback-${r.id.slice(-6)}`}>View Feedback</button>
                    ) : (
                      <button onClick={() => navigate(`/member/feedback/new?source=trade_redeem&id=${r.id}`)} className="text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-1.5 border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10" data-testid={`feedback-btn-${r.id.slice(-6)}`}>Feedback</button>
                    )
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
